import { toast } from 'react-toastify';
import { PoolSizeAdjustmentInput, PoolSizeAdjustmentResult } from 'shared';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { useIdempotentAppMutation } from '../use-idempotent-app-mutation.hook';
import { useInvalidateQueries } from '../use-invalidate-queries.hook';
import { useLocale } from '../use-locale.hook';

export const useAdjustSize = ({
  onSuccess,
  showSuccessToast = true,
}: {
  onSuccess?: () => void;
  /** Off for Edit Pool, which applies a staged adjustment within its own save and toasts that. */
  showSuccessToast?: boolean;
} = {}) => {
  const { messagesLocale } = useLocale();
  // A pool size change moves the pool tables, the cap table / dashboard figures, and — for a
  // formal adjustment — the Events Ledger.
  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_POOLS,
    QueryKey.GET_POOL,
    QueryKey.GET_TERMINATED_POOLS,
    QueryKey.GET_POOLS_BY_STAKEHOLDER,
    QueryKey.GET_STAKEHOLDERS_WITH_GRANTS,
    QueryKey.GET_SHARE_PLANS,
    QueryKey.GET_SHARE_PLAN,
    QueryKey.SHARE_PLAN_STAKEHOLDERS,
    QueryKey.GET_EVENTS,
    QueryKey.GET_EVENT,
    QueryKey.GET_EVENT_TRANSACTIONS,
    QueryKey.GET_SNAPSHOT,
    QueryKey.GET_SNAPSHOTS,
    QueryKey.GET_STATS,
    QueryKey.GET_ISSUED_SHARES,
  );

  const { mutate: adjustSize, isPending } = useIdempotentAppMutation([QueryKey.ADJUST_POOL_SIZE], {
    mutationFn: (
      {
        companyId,
        data,
        poolId,
      }: {
        companyId: string;
        data: PoolSizeAdjustmentInput;
        poolId: string;
      },
      { headers },
    ) =>
      ApiService.post<PoolSizeAdjustmentResult>(
        {
          endpoint: BackendRoute.COMPANIES,
          routePath: [companyId, 'pools', poolId, 'size-adjustments'],
        },
        { body: data, headers },
      ),
    onSuccess: () => {
      if (showSuccessToast) toast.success(messagesLocale['POOL_SIZE_ADJUSTED_SUCCESS']);
      invalidateQuery();
      onSuccess?.();
    },
  });

  return { adjustSize, isPending };
};
